/** Plan de misiones — Camino 2 · rhythm-basics (mapa final 4 misiones). */

import { OPEN_STRINGS, type NoteName } from "../guitarEngine";
import { RHYTHM_01_IM_V5 } from "./rhythm01ImV5";
import { RHYTHM_02_THUMB } from "./rhythm02Thumb";
import { RHYTHM_03_PIM } from "./rhythm03Pim";
import { RHYTHM_04_STRUM } from "./rhythm04Strum";

/** Dedos de ataque en esta fase (sin anular). */
export type AttackFinger = "p" | "i" | "m";

/** Dirección de rasgueo (UI + patrón). */
export type StrumDirection = "down" | "up";

/**
 * Gesto de mano derecha en modo rasgueo.
 * `snap` = chasquido (niveles superiores).
 */
export type StrumGesture = "hand" | "palm" | "mute" | "snap";

/** Modo de UI del studio de ritmo. */
export type RhythmUiMode = "fingers" | "strum";

/** Evento de patrón legacy: dedo o silencio (shh). */
export type RhythmFingerOrRest = AttackFinger | "rest";

/** Cuerda al aire 1–6 (1 = más aguda). */
export type OpenString = 1 | 2 | 3 | 4 | 5 | 6;

/** Clasificación pedagógica del step (UI / badges). */
export type RhythmStepKind = "corto" | "medio" | "resistencia" | "examen";

/**
 * Nota individual del targetPattern (V5).
 * El pitch vive por ataque — no hay rootNote global de la submisión.
 */
export interface RhythmTargetNote {
  type: "attack" | "rest";
  /** Duración en beats (4 redonda, 2 blanca, 1 negra, 0.5 corchea). */
  duration: number;
  /** Pitch esperado, ej. "E4", "B3", "G3". Obligatorio si type === "attack". */
  pitch?: string;
  /** Dedo de ataque (modo fingers). Obligatorio si type === "attack" en fingers. */
  finger?: AttackFinger;
  /** Dirección del rasgueo (modo strum). */
  direction?: StrumDirection;
  /** Gesto: mano / palma / muteo / chasquido. */
  gesture?: StrumGesture;
}

/** Patrón ejecutable: preferir targetPattern (V5); legacy para misiones 2–4. */
export type RhythmPlayPattern = {
  /** Patrón explícito con pitch por nota (V5). */
  targetPattern?: RhythmTargetNote[];
  /** Dedos alineados a ataques (espejo de targetPattern; opcional). */
  attackFingers?: AttackFinger[];
  strokeDurations?: number[];
  fingerPattern?: RhythmFingerOrRest[];
  stringPattern?: OpenString[];
  hitCount?: number;
  bars?: number;
};

export interface RhythmSubMission extends RhythmPlayPattern {
  id: string;
  title: string;
  subtitle?: string;
  goal?: string;
  bpm?: number;
  toleranceMs?: number;
  successRatio?: number;
  kind?: RhythmStepKind;
  /** Drill largo (resistencia / examen). */
  isEndurance?: boolean;
  blockLabel?: string;
  /**
   * Instrucciones del modal intro (3 pasos, lenguaje corporal, sin jerga).
   * Si falta, el studio cae al copy de la misión.
   */
  instructions?: string[];
  /** Tip amigable bajo las instrucciones del modal. */
  tip?: string;
  feedbackOk?: string;
  feedbackError?: string;
}

export interface RhythmPathMission {
  id: string;
  order: number;
  title: string;
  subtitle: string;
  eyebrow: string;
  description: string;
  goal: string;
  instructions: string[];
  professorTip: string;
  feedbackOk: string;
  feedbackError: string;
  timeEstimateMin: number;
  bpm: number;
  fallbackBpm?: number;
  toleranceMs: number;
  successRatio: number;
  requirePitch: boolean;
  /** UI: fingers (p/i/m) o strum (flechas + gesto). */
  uiMode?: RhythmUiMode;
  unlockRequires?: string[];
  muteTip: string;
  subMissions: RhythmSubMission[];
}

export const ATTACK_FINGER_LABEL: Record<AttackFinger, string> = {
  p: "Pulgar",
  i: "Índice",
  m: "Medio",
};

export const STRUM_GESTURE_LABEL: Record<StrumGesture, string> = {
  hand: "Mano",
  palm: "Palma",
  mute: "Muteo",
  snap: "Chasquido",
};

export const STRUM_DIRECTION_LABEL: Record<StrumDirection, string> = {
  down: "Abajo",
  up: "Arriba",
};

export const RHYTHM_STEP_KIND_LABEL: Record<RhythmStepKind, string> = {
  corto: "Corto",
  medio: "Medio",
  resistencia: "Resistencia",
  examen: "Examen",
};

export const OPEN_STRING_LABEL: Record<OpenString, string> = {
  1: "1ª (E agudo)",
  2: "2ª (B)",
  3: "3ª (G)",
  4: "4ª (D)",
  5: "5ª (A)",
  6: "6ª (E grave)",
};

/** Etiqueta corta para UI (número de cuerda, sin jerga de pitch). */
export const OPEN_STRING_SHORT: Record<OpenString, string> = {
  1: "1ª cuerda",
  2: "2ª cuerda",
  3: "3ª cuerda",
  4: "4ª cuerda",
  5: "5ª cuerda",
  6: "6ª cuerda",
};

export const DURATION_BEAT_LABEL: Record<string, string> = {
  "4": "Redonda",
  "2": "Blanca",
  "1": "Negra",
  "0.5": "Corchea",
};

/** Pitch open-string → número de cuerda. */
export const PITCH_TO_OPEN_STRING: Record<string, OpenString> = {
  E4: 1,
  B3: 2,
  G3: 3,
  D3: 4,
  A2: 5,
  E2: 6,
};

export function getOpenStringPitch(openString: OpenString): {
  note: NoteName;
  octave: number;
  midi: number;
} {
  const s = OPEN_STRINGS[6 - openString];
  return { note: s.note, octave: s.octave, midi: s.midi };
}

export function resolvePitchLabel(pitch: string): {
  openString: OpenString;
  note: NoteName;
  octave: number;
  midi: number;
} {
  const raw = pitch.trim();
  const upper = raw.toUpperCase();
  const titled =
    raw.length >= 2
      ? raw[0].toUpperCase() + raw.slice(1)
      : upper;
  const openString: OpenString =
    PITCH_TO_OPEN_STRING[raw] ??
    PITCH_TO_OPEN_STRING[upper] ??
    PITCH_TO_OPEN_STRING[titled] ??
    1;
  const p = getOpenStringPitch(openString);
  return { openString, ...p };
}

export function durationLabel(beats: number): string {
  const key = String(beats);
  return DURATION_BEAT_LABEL[key] ?? `${beats} tiempos`;
}

/** Alternancia i↔m (o m↔i) de longitud n. */
export function alternateIM(n: number, start: "i" | "m" = "i"): AttackFinger[] {
  const out: AttackFinger[] = [];
  let f: AttackFinger = start;
  for (let i = 0; i < n; i++) {
    out.push(f);
    f = f === "i" ? "m" : "i";
  }
  return out;
}

export const RHYTHM_PATH_MISSIONS: RhythmPathMission[] = [
  {
    id: "rhythm-01-im",
    order: 1,
    title: "La Caminata",
    subtitle: "Gimnasio i–m · células rítmicas · V8",
    eyebrow: "Camino 2 · Mano derecha",
    description:
      "Descubrir → Invertir → Consolidar → Resistencia. Corcheas protegidas y células con figuras reales (negra, corchea, blanca).",
    goal: "Alternancia i–m con células mixtas: negras y corcheas entrelazadas sin frustración espacial.",
    instructions: [
      "La pantalla te dice qué cuerda tocar (1ª, 2ª o 3ª).",
      "Negra = un click. Dos corcheas = un click partido. Blanca = dos clicks. Redonda = cuatro.",
      "Los cambios de piso llegan entre células o en figuras largas — nunca partiendo un par de corcheas.",
    ],
    professorTip:
      "Apoya el pulgar en la 6ª. Cuando el motor va al doble, no sumes un salto de cuerda.",
    feedbackOk: "¡Gimnasio listo! i–m ya navegan las tres cuerdas.",
    feedbackError: "Revisa cuerda y ritmo. El metrónomo manda.",
    timeEstimateMin: 80,
    bpm: 60,
    toleranceMs: 150,
    successRatio: 0.75,
    requirePitch: true,
    muteTip: "Tip: una cuerda a la vez. No rasguees.",
    subMissions: RHYTHM_01_IM_V5,
  },
  {
    id: "rhythm-02-thumb",
    order: 2,
    title: "El Peso del Bajo",
    subtitle: "Pulgar · 6ª · 5ª · 4ª",
    eyebrow: "Camino 2 · Mano derecha",
    description:
      "Genera cuerpo en los graves con ataque apoyado del pulgar. i y m descansan.",
    goal: "Navegar 6ª, 5ª y 4ª con peso y pulso, del anclaje al motor de negras.",
    instructions: [
      "Solo el pulgar trabaja. La pantalla marca la cuerda con un círculo.",
      "Ataque hacia el suelo: el dedo cae; no tires de la cuerda.",
      "Ciclo: Descubrir → Invertir → Consolidar → Resistencia.",
    ],
    professorTip:
      "No tires de la cuerda hacia afuera. Deja que el dedo caiga con su propio peso.",
    feedbackOk: "¡Pulgar despierto! Los bajos ya tienen peso.",
    feedbackError: "Revisa cuerda y pulso del pulgar. Reintenta el paso.",
    timeEstimateMin: 36,
    bpm: 60,
    toleranceMs: 150,
    successRatio: 0.75,
    requirePitch: true,
    unlockRequires: ["rhythm-01-im"],
    muteTip: "Tip: i y m descansan. Solo el pulgar trabaja.",
    subMissions: RHYTHM_02_THUMB,
  },
  {
    id: "rhythm-03-pim",
    order: 3,
    title: "Bajo y Respuesta",
    subtitle: "Disociación p · i–m",
    eyebrow: "Camino 2 · Mano derecha",
    description:
      "El pulgar marca el tiempo fuerte en graves; índice y medio responden en agudos.",
    goal: "Coordina bajo y relleno: el patrón real de acompañamiento.",
    instructions: [
      "Tiempo 1: pulgar. Tiempos 2–3–4: respuesta de i y m.",
      "Mira el indicador de dedo: p vs i/m, y el círculo de cuerda.",
      "Los drills largos construyen el motor continuo.",
    ],
    professorTip:
      "Imagina que el pulgar es el bombo. Él manda en el 1; los demás lo siguen.",
    feedbackOk: "¡Bajo y respuesta coordinados!",
    feedbackError: "El orden p–i–m o el bajo se desarmó. Reintenta el paso.",
    timeEstimateMin: 44,
    bpm: 60,
    fallbackBpm: 52,
    toleranceMs: 150,
    successRatio: 0.75,
    requirePitch: true,
    unlockRequires: ["rhythm-02-thumb"],
    muteTip: "Tip: el salto grave→agudo es grande; usa auriculares.",
    subMissions: RHYTHM_03_PIM,
  },
  {
    id: "rhythm-04-strum",
    order: 4,
    title: "Rasgueo Base",
    subtitle: "Muñeca hacia abajo · 6 cuerdas",
    eyebrow: "Camino 2 · Mano derecha",
    description:
      "Movimiento de muñeca hacia abajo (pulgar/palma) por las 6 cuerdas. Motor de la fogata.",
    goal: "Dominar rasgueo hacia abajo y muteo con la palma, a tiempo con el metrónomo.",
    instructions: [
      "Rasguea todas las cuerdas hacia el suelo en un solo gesto.",
      "En los silencios, apaga con la palma. No ataques en el shh.",
      "No hace falta pitch perfecto: importa el ataque a tiempo.",
    ],
    professorTip:
      "Péndulo de muñeca. El brazo casi no empuja; el peso hace el trabajo.",
    feedbackOk: "¡Rasgueo base listo! Ya tienes el motor de la fogata.",
    feedbackError: "Ataca a tiempo hacia abajo. En el silencio, mutea.",
    timeEstimateMin: 24,
    bpm: 60,
    toleranceMs: 160,
    successRatio: 0.75,
    /** Rasgueo: solo timing/transiente; no exige pitch monofónico exacto. */
    requirePitch: false,
    uiMode: "strum",
    unlockRequires: ["rhythm-03-pim"],
    muteTip: "Tip: el muteo con palma es parte del ritmo.",
    subMissions: RHYTHM_04_STRUM,
  },
];

export function getRhythmPathMissionById(id: string): RhythmPathMission | undefined {
  return RHYTHM_PATH_MISSIONS.find((m) => m.id === id);
}

export function isRhythmPathMissionUnlocked(
  mission: RhythmPathMission,
  completedMissionIds: string[],
  preview = false
): boolean {
  if (preview) return true;
  if (!mission.unlockRequires?.length) return true;
  return mission.unlockRequires.every((reqId) => completedMissionIds.includes(reqId));
}

export function getFirstUnlockedRhythmPathMission(
  completedMissionIds: string[]
): RhythmPathMission | undefined {
  return RHYTHM_PATH_MISSIONS.find(
    (m) =>
      isRhythmPathMissionUnlocked(m, completedMissionIds) &&
      !completedMissionIds.includes(m.id)
  );
}

export function getRhythmSubMission(
  mission: RhythmPathMission,
  stepIndex: number
): RhythmSubMission | undefined {
  return mission.subMissions[stepIndex];
}

export function resolveStepBpm(
  mission: RhythmPathMission,
  step: RhythmSubMission,
  bpmOverride?: number
): number {
  return bpmOverride ?? step.bpm ?? mission.bpm;
}

export type ExpandedRhythmHit = {
  finger: AttackFinger | null;
  openString: OpenString;
  pitchLabel: string | null;
  beatOffset: number;
  durationBeats: number;
  isRest: boolean;
  note: NoteName;
  octave: number;
  midi: number;
  direction: StrumDirection | null;
  gesture: StrumGesture | null;
};

function expandFromTargetPattern(
  notes: RhythmTargetNote[]
): ExpandedRhythmHit[] {
  const hits: ExpandedRhythmHit[] = [];
  let beat = 0;
  for (const n of notes) {
    if (n.type === "rest") {
      hits.push({
        finger: null,
        openString: 1,
        pitchLabel: null,
        beatOffset: beat,
        durationBeats: n.duration,
        isRest: true,
        note: "E",
        octave: 4,
        midi: 64,
        direction: null,
        gesture: n.gesture ?? "mute",
      });
    } else {
      const pitch = n.pitch ?? "E4";
      const resolved = resolvePitchLabel(pitch);
      hits.push({
        finger: n.finger ?? "i",
        openString: resolved.openString,
        pitchLabel: pitch,
        beatOffset: beat,
        durationBeats: n.duration,
        isRest: false,
        note: resolved.note,
        octave: resolved.octave,
        midi: resolved.midi,
        direction: n.direction ?? "down",
        gesture: n.gesture ?? (n.finger === "p" ? "hand" : "hand"),
      });
    }
    beat += n.duration;
  }
  return hits;
}

function isRestToken(f: RhythmFingerOrRest): f is "rest" {
  return f === "rest";
}

/** Expande un patrón (targetPattern V5 o legacy) a golpes / silencios. */
export function expandRhythmHits(pattern: RhythmPlayPattern): ExpandedRhythmHit[] {
  if (pattern.targetPattern?.length) {
    return expandFromTargetPattern(pattern.targetPattern);
  }

  const hits: ExpandedRhythmHit[] = [];
  const fingers = pattern.fingerPattern ?? [];
  const n = fingers.length;
  if (n === 0) return hits;

  const durations = pattern.strokeDurations ?? [1];
  const strings = pattern.stringPattern ?? [1];
  const patternBeats = durations.reduce((a, b) => a + b, 0);
  const targetSoundHits =
    pattern.hitCount != null && pattern.hitCount > 0 ? pattern.hitCount : null;
  const totalBeats =
    targetSoundHits != null ? Infinity : (pattern.bars ?? 1) * 4;

  let beat = 0;
  let i = 0;
  let soundCount = 0;
  const safety = 800;

  while (i < safety) {
    if (targetSoundHits != null && soundCount >= targetSoundHits) break;
    if (targetSoundHits == null && beat >= totalBeats - 1e-6) break;

    const idx = i % n;
    const token = fingers[idx];
    const openString =
      strings[idx % strings.length] ?? strings[0] ?? 1;
    const dur = durations[idx % durations.length];
    const pitch = getOpenStringPitch(openString);
    const rest = isRestToken(token);

    hits.push({
      finger: rest ? null : token,
      openString,
      pitchLabel: rest ? null : `${pitch.note}${pitch.octave}`,
      beatOffset: beat,
      durationBeats: dur,
      isRest: rest,
      note: pitch.note,
      octave: pitch.octave,
      midi: pitch.midi,
      direction: rest ? null : "down",
      gesture: rest ? "mute" : "hand",
    });
    if (!rest) soundCount += 1;

    beat += dur;
    i += 1;
    if (patternBeats <= 0) break;
  }
  return hits;
}

export function formatFingerPatternLabel(
  pattern: RhythmPlayPattern
): string {
  if (pattern.targetPattern?.length) {
    const fingers = pattern.targetPattern.map((n) =>
      n.type === "rest" ? "shh" : (n.finger ?? "?")
    );
    const shown = fingers.slice(0, 8);
    return shown.join(" · ") + (fingers.length > 8 ? "…" : "");
  }
  return (pattern.fingerPattern ?? [])
    .map((f) => (f === "rest" ? "shh" : f))
    .join(" · ");
}

export function formatPitchPatternLabel(pattern: RhythmPlayPattern): string {
  if (pattern.targetPattern?.length) {
    const strings = pattern.targetPattern
      .filter((n) => n.type === "attack" && n.pitch)
      .map((n) => resolvePitchLabel(n.pitch!).openString);
    const unique = [...new Set(strings)];
    if (unique.length === 0) return "";
    if (unique.length <= 4) return unique.map((s) => `${s}ª`).join(" · ");
    return `${unique.slice(0, 3).map((s) => `${s}ª`).join(" · ")}…`;
  }
  const strings = pattern.stringPattern ?? [];
  return [...new Set(strings)].map((s) => `${s}ª`).join(" · ");
}

export function countSoundHits(pattern: RhythmPlayPattern): number {
  return expandRhythmHits(pattern).filter((h) => !h.isRest).length;
}
